import { documentUploadBaseApi } from '@/services/base/documentUpload';
import {
  UploadCasPracticeAnswerRequestType,
  UploadCasPracticeAnswerResponseType,
} from '@/types/casServices';
import { paths } from '@/types/schema';
import {
  AddRemarkMutationResponse,
  FileUploadResponse,
  UploadCspLogoRequestType,
  UploadCspLogoResponseType,
} from '@/types/Services';

import { AxiosBaseQueryArgsType } from './axios/axiosBaseQuery';

import { transformResponse } from './utils';

export type AddStudentReferralLeadStatusRemarksResponseType =
  paths['/lead/{studentId}/remark']['post']['responses']['200']['schema'];
export type AddStudentReferralLeadStatusRemarksRequestType =
  paths['/lead/{studentId}/remark']['post']['parameters']['query'] &
    paths['/lead/{studentId}/remark']['post']['parameters']['formData'] & {
      fileName?: string;
      studentId: number;
    };

export const documentUploadApi = documentUploadBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    upload: builder.mutation<
      FileUploadResponse,
      { file: File; key: string; studentId: number }
    >({
      query: ({ file, key, studentId }) => {
        const queryObj: any = {
          url: `/v1/documents/doc/${key}/${studentId}`,
          method: `POST`,
        };
        const formData = new FormData();
        formData.append(`file`, file);
        queryObj[`body`] = formData;
        return queryObj;
      },
      invalidatesTags: [],
    }),
    uploadAndSave: builder.mutation<
      FileUploadResponse,
      {
        file: File;
        key: string;
        applicationId?: number;
        fileLabel?: string;
        studentId: number;
        referenceLabel?: string;
        referenceLabelId?: number;
        id?: number;
      }
    >({
      query: ({
        file,
        key,
        applicationId,
        studentId,
        fileLabel,
        referenceLabel,
        referenceLabelId,
        id,
      }) => {
        const queryObj: any = {
          url: `/v1/documents`,
          method: `POST`,
        };

        if (!(file && studentId && key)) {
          return;
        }

        const formData = new FormData();
        formData.append(`file`, file, file?.name?.split(`,`)?.join(`_`));
        formData.append(`keyName`, key);
        if (applicationId) {
          formData.append(`applicationId`, String(applicationId));
        }
        if (fileLabel) {
          formData.append(`fileLabel`, fileLabel);
        }
        if (referenceLabel) {
          formData.append(`referenceLabel`, referenceLabel);
        }
        if (referenceLabelId) {
          formData.append(`referenceLabelId`, String(referenceLabelId));
        }
        if (id) {
          formData.append(`documentId`, String(id));
        }
        formData.append(`studentId`, String(studentId));
        queryObj[`body`] = formData;
        return queryObj;
      },
      invalidatesTags: [`documents`],
    }),
    addRemark: builder.mutation<
      boolean,
      {
        file?: File;
        comment?: string;
        applicationId: number;
        keyName?: string;
        fileLabel?: string;
      }
    >({
      query: ({ file, comment, applicationId, keyName, fileLabel }) => {
        if (!file && !comment) return null;
        const queryObj: any = {
          url: `/applications/remark`,
          method: `POST`,
        };
        const formData = new FormData();
        formData.append(`remark`, comment || ``);
        formData.append(`givenByTeam`, `CSP`);
        formData.append(`applicationId`, String(applicationId));
        if (file) {
          formData.append(`file`, file);
          keyName && formData.append(`keyName`, keyName || ``);
          fileLabel && formData.append(`fileLabel`, fileLabel || ``);
        }
        queryObj[`body`] = formData;
        return queryObj;
      },
    }),
    // need to think about automatic refetching using tags
    addStudentRemark: builder.mutation<
      AddRemarkMutationResponse,
      { file?: File; remark?: string; studentId: number; fileName?: string }
    >({
      query: ({ file, remark, studentId }) => {
        if (!file && !remark) return null;
        const queryObj: any = {
          url: `/v1/students/remark`,
          method: `POST`,
        };
        const formData = new FormData();
        formData.append(`remark`, remark || ``);
        formData.append(`givenByTeam`, `CSP`);
        formData.append(`studentId`, `${studentId}`);

        if (file) {
          formData.append(`file`, file);
        }
        queryObj[`body`] = formData;
        return queryObj;
      },
      // TODO:: need to check why automatic refetch not working
      invalidatesTags: [`Student`, `Remarks`, `documents`],
    }),
    uploadCspLogo: builder.mutation<
      UploadCspLogoResponseType,
      UploadCspLogoRequestType
    >({
      query: ({ file, cspId }) => {
        if (!file && !cspId) return null;
        const queryObj: any = {
          url: `/v1/csps/${cspId}/logo`,
          method: `PUT`,
        };

        const formData = new FormData();
        formData.append(`file`, file as File);
        queryObj[`body`] = formData;
        return queryObj;
      },
      transformResponse: ({ success, data, message }) => {
        if (!success) {
          return {
            data: null,
            message,
            apiError: true,
          } as unknown as UploadCspLogoResponseType;
        } else {
          return {
            success: true,
            data,
            message: null,
          } as unknown as UploadCspLogoResponseType;
        }
      },
    }),
    addStudentLGReferralRemark: builder.mutation<
      AddStudentReferralLeadStatusRemarksResponseType,
      AddStudentReferralLeadStatusRemarksRequestType
    >({
      query: ({ studentId, ...payload }) => {
        const queryObj: any = {
          url: `/lead/${studentId}/remark`,
          method: `POST`,
        };

        const formData = new FormData();

        if (payload?.file) {
          formData.append(`file`, payload?.file as File);
        }
        if (payload?.fileName) {
          formData.append(`fileName`, payload?.fileName);
        }

        formData.append(`remark`, payload?.remark);

        queryObj[`body`] = formData;
        return queryObj;
      },
      transformResponse: transformResponse,
    }),
    uploadCasTestPrepAnswer: builder.mutation<
      UploadCasPracticeAnswerResponseType,
      UploadCasPracticeAnswerRequestType
    >({
      query: ({ keyName, guid, ...payload }) => {
        const queryObj: AxiosBaseQueryArgsType = {
          url: `/documents/doc/no-auth/${keyName}/${guid}`,
          method: `POST`,
          headers: {
            'Content-Type': `multipart/form-data`,
          },
        };
        const formData = new FormData();
        if (payload?.file) {
          formData.append(`file`, payload?.file as File);
        }
        queryObj[`body`] = formData;
        return queryObj;
      },
    }),
  }),
});

export const {
  useUploadMutation,
  useAddRemarkMutation,
  useUploadAndSaveMutation,
  useAddStudentRemarkMutation,
  useUploadCspLogoMutation,
  useAddStudentLGReferralRemarkMutation,
  useUploadCasTestPrepAnswerMutation,
} = documentUploadApi;
